'use client'

import { Button, Link, LinkProps, Typography } from '@mui/material'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import dayjs from 'dayjs'
import { ReactElement, useState } from 'react'
import { DownloadIcon } from '../Icon/catalog'
import { Invoice } from './InvoicesTable.stories'

export type InvoicesTableProps = { data: readonly Invoice[] }

const tableRowSx = { '&:last-child td, &:last-child th': { border: 0 } }
const invoiceNumberTableCellSx = { wordBreak: 'break-all' }
const linkProps: Partial<LinkProps> = {
    sx: {
        display: 'flex',
        gap: '3px',
    },
    download: true,
    underline: 'hover',
}

const InvoicesTable = ({ data }: InvoicesTableProps): ReactElement => {
    const [displayedRows, setDisplayedRows] = useState(3)
    const isCollapsible = data.length > 3
    const isCollapsed = isCollapsible && displayedRows < data.length

    const handleClick = () => {
        const remainingInvoices = data.length - displayedRows
        if (remainingInvoices >= 10) {
            setDisplayedRows(displayedRows + 10)
        } else {
            setDisplayedRows(data.length)
        }
    }
    return (
        <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table>
                <TableHead>
                    <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}>
                        <TableCell>
                            <Typography component="span" variant="h4">
                                Invoice date
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography component="span" variant="h4">
                                Invoice number
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography component="span" variant="h4">
                                Amount
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography component="span" variant="h4">
                                Download
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.slice(0, displayedRows).map((invoice: Invoice) => {
                        return (
                            <TableRow sx={tableRowSx} key={invoice.invoiceNumber}>
                                <TableCell component="th" scope="row">
                                    {dayjs(invoice.date).format('DD MMM YYYY')}
                                </TableCell>
                                <TableCell sx={invoiceNumberTableCellSx}>{invoice.invoiceNumber}</TableCell>
                                <TableCell>{`${invoice.amount} ${invoice.currency}`}</TableCell>
                                <TableCell>
                                    <Link {...linkProps} href={invoice.downloadUrl}>
                                        <DownloadIcon />
                                        <span className="desktop-only">{`Download as `}</span>PDF
                                    </Link>
                                </TableCell>
                            </TableRow>
                        )
                    })}
                    {isCollapsed ? (
                        <TableRow>
                            <TableCell colSpan={4} sx={{ textAlign: 'center' }}>
                                <Button onClick={handleClick}>Show more</Button>
                            </TableCell>
                        </TableRow>
                    ) : null}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default InvoicesTable
